import classNames from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'react';

export type Pos = { x: number; y: number };

type Props = {
    disabled?: boolean;
    style?: React.CSSProperties;
    className?: string;
    children: React.ReactNode;
    onDragStart?: (event: PointerEvent) => void;
    onDragMove?: (delta: Pos) => void;
    onDragEnd?: (delta: Pos) => void;
};
export function Draggable({
    disabled,
    style,
    className,
    children,
    onDragStart,
    onDragEnd,
    onDragMove,
}: Props) {
    const [startPos, setStartPos] = useState<Pos | null>(null);
    const isDragging = useRef(false);
    const element = useRef<HTMLDivElement | null>();
    const onPointerDown = useCallback(
        (event: PointerEvent) => {
            onDragStart?.(event);
            if (event.defaultPrevented) {
                return;
            }
            isDragging.current = true;
            event.preventDefault();
            if (event.currentTarget instanceof HTMLElement) {
                event.currentTarget.setPointerCapture(event.pointerId);
            }
            setStartPos({ x: event.clientX, y: event.clientY });
        },
        [onDragStart],
    );
    const onPointerUp = useCallback(
        (event: PointerEvent) => {
            if (event.currentTarget instanceof HTMLElement) {
                event.currentTarget.releasePointerCapture(event.pointerId);
            }
            isDragging.current = false;
            onDragEnd?.({
                x: event.clientX - startPos.x,
                y: event.clientY - startPos.y,
            });
        },
        [onDragEnd],
    );
    const onPointerMove = useCallback(
        (event: PointerEvent) => {
            if (
                event.currentTarget instanceof HTMLElement &&
                !event.currentTarget.hasPointerCapture(event.pointerId)
            )
                return;

            event.preventDefault();
            onDragMove?.({ x: event.movementX, y: event.movementY });
        },
        [onDragMove],
    );
    const onTouchMove = useCallback((event: TouchEvent) => {
        if (isDragging.current) {
            event.preventDefault();
        }
    }, []);
    useEffect(() => {
        if (element.current == null) return;
        if (!disabled) {
            element.current.addEventListener('pointerdown', onPointerDown);
            element.current.addEventListener('pointerup', onPointerUp);
            element.current.addEventListener('pointermove', onPointerMove);
            // React does not support adding passive listeners, so we have to handle this manually.
            element.current.addEventListener('touchmove', onTouchMove, {
                passive: false,
            });
        }
        return () => {
            if (element.current == null) return;
            element.current.removeEventListener('pointerdown', onPointerDown);
            element.current.removeEventListener('pointerup', onPointerUp);
            element.current.removeEventListener('pointermove', onPointerMove);
            element.current.removeEventListener('touchmove', onTouchMove);
        };
    }, [
        disabled,
        element,
        onPointerDown,
        onPointerUp,
        onPointerMove,
        onTouchMove,
    ]);

    return (
        <div
            ref={element}
            style={style}
            className={classNames('inline-block', className)}
        >
            {children}
        </div>
    );
}
